import React, { useState } from 'react';
import { Clock, Search, ArrowUp, Cookie, Settings, Shield, Activity } from 'lucide-react';

interface CookieCategory {
  id: string;
  title: string;
  description: string;
  required: boolean;
}

const Cookies: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  const cookieCategories: CookieCategory[] = [
    {
      id: 'essential',
      title: 'Essential Cookies',
      description: 'Required for basic site functionality. These cookies ensure secure login, session management, and core features work properly.',
      required: true
    },
    {
      id: 'functional',
      title: 'Functional Cookies',
      description: 'Remember your preferences and personalize your experience. These help us remember your settings and provide enhanced features.',
      required: false
    },
    {
      id: 'analytics',
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors use our site. This data is anonymized and helps us improve our services.',
      required: false
    }
  ];

  // Handle scroll events for back-to-top button
  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      <div className="container mx-auto px-4 md:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-['Poppins']">
            Cookie Policy
          </h1>
          
          <div className="flex items-center gap-4 text-gray-600 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>5 min read</span>
            </div>
            <div className="h-1 w-1 bg-gray-300 rounded-full"></div>
            <span>Last updated: March 15, 2025</span>
          </div>

          {/* Search */}
          <div className="relative mb-12">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search cookie policy..."
              className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-[#F4B6A1] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Introduction */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Cookie className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">What Are Cookies?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Cookies are small text files stored on your device when you visit our website. 
              They help us provide you with a better experience by remembering your preferences, 
              maintaining secure sessions, and understanding how you use our service.
            </p>
          </section>

          {/* Cookie Categories */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Settings className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">Cookie Categories</h2>
            </div>
            <div className="space-y-6">
              {cookieCategories.map((category) => (
                <div key={category.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">{category.title}</h3>
                    {category.required && (
                      <span className="text-sm bg-gray-100 text-gray-600 px-3 py-1 rounded-full">Required</span>
                    )}
                  </div>
                  <p className="text-gray-600">{category.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Cookie Management */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Shield className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">Managing Your Cookie Preferences</h2>
            </div>
            <p className="text-gray-600 mb-6">
              You can control cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc pl-5 space-y-2 text-gray-600 mb-6">
              <li>Block all cookies</li>
              <li>Accept only first-party cookies</li>
              <li>Delete cookies when you close your browser</li>
            </ul>
            <p className="text-gray-600">
              Please note that blocking essential cookies will affect the functionality of our service.
            </p>
          </section>

          {/* Third-Party Cookies */}
          <section className="bg-white rounded-xl shadow-md p-6 md:p-8 mb-8">
            <div className="flex items-center mb-6">
              <Activity className="w-8 h-8 text-[#F4B6A1] mr-3" />
              <h2 className="text-2xl font-semibold">Third-Party Cookies</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Some features of our service rely on third-party services that may set their own cookies. 
              These cookies are governed by the respective privacy policies of these services.
            </p>
            <p className="text-gray-600">
              We regularly review our third-party partnerships to ensure they maintain high privacy standards.
            </p>
          </section>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#F4B6A1] hover:bg-[#f3a28a] text-white p-3 rounded-full shadow-lg transition-colors"
          aria-label="Back to top"
        >
          <ArrowUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Cookies;